import { getSimpleDateUnified } from 'src/utils/date';
import { EventsCollectionQueryModel } from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { DateTime } from 'src_common/utils/time/time';

export interface UserViewType {
    sports: Record<string, boolean | undefined>;
    competitions: Record<number, boolean | undefined>;
    dates: Record<string, boolean | undefined>;
}

const listToViewAllOpen = (collection: EventsCollectionQueryModel | null): UserViewType => {
    const result: UserViewType = {
        sports: {},
        competitions: {},
        dates: {},
    };
    if (collection !== null) {
        for (const sport of collection.value.sports) {
            result.sports[sport.id] = true;

            for (const competition of sport.competitions) {
                result.competitions[competition.id] = true;
            }
        }
    }
    return result;
};

const listToViewTopOpen = (
    collection: EventsCollectionQueryModel | null,
    topOpen: number,
    getTranslation: (key: string, defaultText: string) => string
): UserViewType => {
    const result: UserViewType = {
        sports: {},
        competitions: {},
        dates: {},
    };

    let counter = 0;

    const getVisibleFlag = (): boolean => {
        if (counter < topOpen) {
            counter++;
            return true;
        }

        return false;
    };
    if (collection !== null) {
        for (const sport of collection.value.sports) {
            let isVisibleSport = false;

            for (const competition of sport.competitions) {
                let isVisibleCompetition = getVisibleFlag();

                if (isVisibleCompetition) {
                    isVisibleSport = true;
                }

                result.competitions[competition.id] = isVisibleCompetition;

                for (const event of competition.events) {
                    const isVisibleEvent = true;

                    // if (isVisibleEvent) {
                    //     isVisibleCompetition = true;
                    // }

                    const eventDateKey = getEventDateKey(event, getTranslation);
                    result.dates[eventDateKey] = isVisibleEvent;
                }
            }

            result.sports[sport.id] = isVisibleSport;
        }
    }

    return result;
};

const getIsVisibleSport = (userView: UserViewType, sportId: string): boolean => {
    return userView.sports[sportId] ?? false;
};

const getIsVisibleCompetition = (userView: UserViewType, competionId: number): boolean => {
    return userView.competitions[competionId] ?? false;
};

const getIsVisibleDate = (userView: UserViewType, date: string): boolean => {
    return userView.dates[date] ?? false;
};

const getEventDateKey = (
    event: EventListGroupEventItemType,
    getTranslation: (key: string, defaultText: string) => string
) => {
    const eventModel = event.id2.getEventModel();

    return eventModel === null
        ? getSimpleDateUnified(DateTime.from(event.startTime)?.toISOString() ?? 'time error', getTranslation)
        : getSimpleDateUnified(eventModel.timeSettingsStartTime, getTranslation);
};

const calculateNewEventsGroup = (
    collection: EventsCollectionQueryModel | null,
    userView: UserViewType,
    getTranslation: (key: string, defaultText: string) => string
): UserViewType => {
    const result: UserViewType = {
        sports: {},
        competitions: {},
        dates: {},
    };
    if (collection !== null) {
        for (const sport of collection.value.sports) {
            result.sports[sport.id] = getIsVisibleSport(userView, sport.id);

            for (const competition of sport.competitions) {
                result.competitions[competition.id] = getIsVisibleCompetition(userView, competition.id);

                for (const event of competition.events) {
                    const eventDateKey = getEventDateKey(event, getTranslation);
                    result.dates[eventDateKey] = getIsVisibleDate(userView, eventDateKey);
                }
            }
        }
    }

    return result;
};

const calculateNewEventsForSpecialSportGroup = (
    collection: EventsCollectionQueryModel | null,
    topOpen: number
): UserViewType => {
    const result: UserViewType = {
        sports: {},
        competitions: {},
        dates: {},
    };

    const competitions = collection?.value.sports.map((elem) => elem.competitions).flat() ?? [];

    for (const [key, competition] of competitions.entries()) {
        result.competitions[competition.id] = key < topOpen;
    }

    return result;
};

export const calculateNextUserView = (
    userView: UserViewType | null,
    collection: EventsCollectionQueryModel | null,
    defaultOpen: 'all-open' | number,
    isSpecial: boolean,
    getTranslation: (key: string, defaultText: string) => string
): UserViewType => {
    if (userView === null) {
        if (defaultOpen === 'all-open') {
            return listToViewAllOpen(collection);
        } else {
            if (isSpecial) {
                return calculateNewEventsForSpecialSportGroup(collection, defaultOpen);
            }
            return listToViewTopOpen(collection, defaultOpen, getTranslation);
        }
    } else {
        return calculateNewEventsGroup(collection, userView, getTranslation);
    }
};
